import { useCallback, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api from "../axiosSetup";
import moment from "moment";
import DataTable from "./DataTable";
// Der Bestaetigungsdialog braucht die Modal-Klassen. Explizit importiert,
// damit er nicht davon abhaengt, dass eine Nachbarkomponente die Datei laedt.
import "./css/Modal-Style.css";

const formatValue = (val, type) => {
  if (type === "date" && val) return moment(val).isValid() ? moment(val).format("DD.MM.YYYY") : val;
  if (type === "bool") return val === true || val === 1 ? "Ja" : "Nein";
  return val || "—";
};

const ZRPersonnelFileList = ({ memberID }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [entryToDelete, setEntryToDelete] = useState(null);

  const fetchEntries = useCallback(async () => {
    try {
      const token = localStorage.getItem("authToken");
      const res = await api.get(`/api/all-personnel-entries-zunftrat/${memberID}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setEntries(res.data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [memberID]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  const handleDelete = async () => {
    if (!entryToDelete) return;
    try {
      const token = localStorage.getItem("authToken");
      await api.delete(`/api/delete-personnel-entry-zunftrat/${entryToDelete.ID}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMessage({ type: "success", text: "Eintrag wurde gelöscht." });
      setEntryToDelete(null);
      fetchEntries();
    } catch (err) {
      setMessage({
        type: "danger",
        text: err.response?.data?.message || "Der Eintrag konnte nicht gelöscht werden.",
      });
      setEntryToDelete(null);
    }
  };

  const columns = [
    {
      key: "typName",
      label: "Typ",
      render: (v) => v ? <span className="dt-badge dt-badge-info">{v}</span> : <span className="dt-muted">—</span>,
    },
    {
      key: "data",
      label: "Beschreibung",
      sortable: false,
      render: (rawData) => {
        if (!rawData) return <span className="dt-muted">—</span>;
        try {
          const parsed = JSON.parse(rawData);
          return (
            <div style={{ fontSize: 13, lineHeight: 1.6 }}>
              {Object.entries(parsed).map(([key, val]) => {
                const label = val["frontend-name"] || key;
                const display = formatValue(val.value, val.type);
                return (
                  <div key={key}>
                    <strong>{label}:</strong> {display}
                  </div>
                );
              })}
            </div>
          );
        } catch {
          return <span className="dt-muted">{String(rawData)}</span>;
        }
      },
      searchValue: (rawData) => {
        if (!rawData) return "";
        try {
          const parsed = JSON.parse(rawData);
          return Object.values(parsed)
            .map((v) => String(v.value ?? ""))
            .join(" ");
        } catch {
          return String(rawData);
        }
      },
    },
    {
      key: "erstellt_von",
      label: "Erstellt von",
      render: (v) => v || <span className="dt-muted">—</span>,
    },
    {
      key: "erstellt_am",
      label: "Erstellt am",
      render: (v) =>
        v && moment(v).isValid() ? moment(v).format("DD.MM.YYYY HH:mm") : <span className="dt-muted">—</span>,
    },
  ];

  const actions = (entry) => (
    <div className="dt-actions">
      <button
        className="dt-btn dt-btn-primary"
        onClick={() => navigate("/zr-personnell-entry-create", { state: { entry, memberID } })}
      >
        Bearbeiten
      </button>
      <button className="dt-btn dt-btn-danger" onClick={() => setEntryToDelete(entry)}>
        Löschen
      </button>
    </div>
  );

  const headerActions = (
    <button
      className="dt-btn dt-btn-success"
      onClick={() => navigate("/zr-personnell-entry-create", { state: { memberID } })}
    >
      + Eintrag erstellen
    </button>
  );

  return (
    <div className="mt-4">
      {location.state?.message && (
        <div className="alert alert-warning mb-3">{location.state.message}</div>
      )}
      {message && (
        <div className={`alert alert-${message.type} mb-3`}>{message.text}</div>
      )}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0">Personalakte</h4>
      </div>
      <DataTable
        data={entries}
        columns={columns}
        loading={loading}
        error={error}
        actions={actions}
        headerActions={headerActions}
        searchPlaceholder="Eintrag suchen…"
        emptyText="Keine Einträge vorhanden."
        initialPageSize={10}
        pageSizeOptions={[10, 25, 50]}
      />

      {entryToDelete && (
        <div className="modal-overlay" onClick={() => setEntryToDelete(null)}>
          <div className="modal-box animate-in" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h5 className="modal-title text-dark">Eintrag löschen</h5>
              <button className="modal-close" onClick={() => setEntryToDelete(null)}>
                &times;
              </button>
            </div>
            <div className="modal-body text-dark">
              <p>
                Soll der Eintrag <strong>{entryToDelete.typName || "ohne Typ"}</strong> vom{" "}
                <strong>
                  {entryToDelete.erstellt_am && moment(entryToDelete.erstellt_am).isValid()
                    ? moment(entryToDelete.erstellt_am).format("DD.MM.YYYY")
                    : "unbekannten Datum"}
                </strong>{" "}
                wirklich gelöscht werden?
                <br />
                Diese Aktion kann nicht rückgängig gemacht werden und wird im Aktions-Log
                festgehalten.
              </p>
            </div>
            <div className="modal-footer mt-4 d-flex gap-2 justify-content-end">
              <button className="btn btn-secondary" onClick={() => setEntryToDelete(null)}>
                Abbrechen
              </button>
              <button className="btn btn-danger" onClick={handleDelete}>
                Löschen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ZRPersonnelFileList;
